import axios from 'axios';
import conf from 'src/conf/conf';
import userManager from 'src/auth/userManager';
import { I18n } from 'src/translations/i18n';
const MU4_PROXY_PATH = '/mu4proxy';
const mu4ProxyAxios = axios.create({
    baseURL: conf.api
});
const attachAuthAndLanguage = async (config) => {
    const user = await userManager.getUser();
    if (user?.access_token) {
        config.headers.Authorization = `Bearer ${user.access_token}`;
    }
    if (I18n.language?.iso6391) {
        config.headers['accept-language'] = I18n.language.iso6391;
    }
    return config;
};
const handleUnauthorized = (error) => {
    if (error.response?.status === 401) {
        userManager.removeUser();
        window.location.reload();
    }
    return Promise.reject(error);
};
mu4ProxyAxios.interceptors.request.use(attachAuthAndLanguage);
mu4ProxyAxios.interceptors.response.use((response) => response, handleUnauthorized);
// --- API Functions ---
// Through the WTS proxy like every other MU4 call (and like CC), so the profiles come from the same company as the
// keys and mappings they are given with
export function getAccessGroups() {
    return mu4ProxyAxios.get(`${MU4_PROXY_PATH}/api/AccessGroup`, { params: { cursorId: 0, maxCount: 10000 } });
}
export function createAccessUser(data) {
    return mu4ProxyAxios.post(`${MU4_PROXY_PATH}/api/AccessUser`, data);
}
export function getAccessUser(id) {
    return mu4ProxyAxios.get(`${MU4_PROXY_PATH}/api/AccessUser/${id}`);
}
export function updateAccessUser(id, data) {
    return mu4ProxyAxios.put(`${MU4_PROXY_PATH}/api/AccessUser/${id}`, data);
}
export function deleteAccessUser(id) {
    return mu4ProxyAxios.delete(`${MU4_PROXY_PATH}/api/AccessUser/${id}`);
}
// GET /api/AccessKey has no user filter (only a cursor), so on a large company the first page never reaches a new
// key. The v2 query filters on the server; it is paged through until the server reports the last page.
const USER_RECORDS_PAGE_SIZE = 100;
const USER_RECORDS_MAX_PAGES = 1000;
export async function getAccessKeysByUser(userId) {
    const keys = [];
    for (let pageNumber = 1; pageNumber <= USER_RECORDS_MAX_PAGES; pageNumber++) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await mu4ProxyAxios.post(`${MU4_PROXY_PATH}/api/v2/AccessKey/query`, { userIdList: [userId], pageNumber, pageSize: USER_RECORDS_PAGE_SIZE });
        // The server clamps an out-of-range page back to the last one rather than failing
        if (data.currentPage < pageNumber)
            break;
        keys.push(...(data.results || []));
        if (!data.hasNext)
            break;
    }
    // The query sorts by sequence number (typed keys first) and has no sort by id - keep the creation order
    return keys.sort((a, b) => a.id - b.id);
}
export function createAccessKey(data) {
    return mu4ProxyAxios.post(`${MU4_PROXY_PATH}/api/AccessKey`, data);
}
export function getAccessKey(id) {
    return mu4ProxyAxios.get(`${MU4_PROXY_PATH}/api/AccessKey/${id}`);
}
export function updateAccessKey(id, data) {
    return mu4ProxyAxios.put(`${MU4_PROXY_PATH}/api/AccessKey/${id}`, data);
}
// Detach: a card that came from the free key pool goes back there with its sequence number; a typed key is deleted
export function deleteAccessKey(id) {
    return mu4ProxyAxios.delete(`${MU4_PROXY_PATH}/api/AccessKey/${id}`);
}
export function queryIdKeys(params) {
    return mu4ProxyAxios.post(`${MU4_PROXY_PATH}/api/IdKey/query`, params);
}
// Issues a pool card, found by its code. 404 - the code is not in the pool; 409 - the card is issued to someone else.
export function assignIdKey(data) {
    return mu4ProxyAxios.post(`${MU4_PROXY_PATH}/api/IdKey/assign`, data);
}
// Same as the keys: GET /api/UserGroupMapping has no user filter. The query filters on the server and is read by
// cursor - each page continues after the last id received - until a page comes back short.
export async function getUserGroupMappingsByUser(userId) {
    const mappings = [];
    let cursorId = 0;
    for (let page = 1; page <= USER_RECORDS_MAX_PAGES; page++) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await mu4ProxyAxios.post(`${MU4_PROXY_PATH}/api/UserGroupMapping/query`, { userId, cursorId, maxCount: USER_RECORDS_PAGE_SIZE });
        const records = data || [];
        const lastId = records.length ? records[records.length - 1].id : cursorId;
        if (!records.length || lastId <= cursorId)
            break;
        mappings.push(...records);
        if (records.length < USER_RECORDS_PAGE_SIZE)
            break;
        cursorId = lastId;
    }
    return mappings;
}
export function createUserGroupMapping(data) {
    return mu4ProxyAxios.post(`${MU4_PROXY_PATH}/api/UserGroupMapping`, data);
}
export function getUserGroupMapping(id) {
    return mu4ProxyAxios.get(`${MU4_PROXY_PATH}/api/UserGroupMapping/${id}`);
}
export function updateUserGroupMapping(id, data) {
    return mu4ProxyAxios.put(`${MU4_PROXY_PATH}/api/UserGroupMapping/${id}`, data);
}
export function deleteUserGroupMapping(id) {
    return mu4ProxyAxios.delete(`${MU4_PROXY_PATH}/api/UserGroupMapping/${id}`);
}
